'use client'

import Link from 'next/link'
import { useId } from 'react'

import { AUTH_ACCENT_GRADIENT, authInputClass } from '@/components/molecules/auth-card'
import { MICRO } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The registration form's three controls, kept out of the page.
 *
 * They are here for size rather than reuse: the form grew a second and third
 * checkbox and a pair of invoice fields, and a copy of the markup per checkbox
 * is exactly how two of them end up looking different from each other.
 */

/**
 * One labelled text field.
 *
 * The label used to be a bare `<label>` beside a bare `<input>` — no `id`, no
 * `htmlFor`, so nothing tied them together. It LOOKED labelled and was not: a
 * screen reader announced "edit text" with no name on all five fields of the
 * sign-up form, clicking the word did not focus the box, and the browser had no
 * name to autofill against. The `useId` pairing below is the whole fix.
 *
 * The error message is wired up too, and for the same reason. `aria-invalid`
 * tells assistive tech the value was rejected and `aria-describedby` says why —
 * without them the sentence is on screen, in red, and simply not announced,
 * which is the single most common way a form becomes impossible to complete
 * without sight.
 *
 * UNCONTROLLED, deliberately. A `value` bound to React state is overwritten at
 * hydration, which silently erases everything typed before the bundle finished
 * loading — the first second of the page on a slow connection. The caller
 * mirrors `onChange` into state for validation and reads the DOM once on mount
 * to pick up what was typed before that; see `register-page.client.tsx`.
 *
 * `id` is supplied by the caller when it needs to find the field again for that
 * reading, and falls back to a generated one when it does not.
 *
 * ## IT IS THE SIGN-IN FIELD NOW, NOT A LOOKALIKE
 *
 * The box used to carry its own copy of the sign-in card's class string plus a
 * `focus` prop threaded down from the page, which is how `/pl/register` ended
 * up with a fourth private spelling of the brand pink. It wears
 * `authInputClass` itself now — and `flou-auth-field` with it, so the password
 * manager's wash and the keyboard focus outline behave here exactly as they do
 * at `/pl/login`. Owner's call: *„rejestracja ma być w stylu logowania
 * dokładnie"*.
 */
export function Input({
  label,
  id,
  onChange,
  type = 'text',
  autoComplete,
  rightSlot,
  error,
}: {
  label: string
  id?: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type?: string
  autoComplete?: string
  rightSlot?: React.ReactNode
  error?: string
}) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const errorId = `${fieldId}-error`

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className={cn(MICRO, 'font-medium text-white/55')}>
        {label}
      </label>
      <div className="relative">
        <input
          id={fieldId}
          type={type}
          onChange={onChange}
          autoComplete={autoComplete}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            authInputClass,
            'flou-auth-field',
            // 16px, and not for looks: anything under it makes iOS Safari zoom
            // the viewport the moment the field takes focus.
            'text-[16px] sm:text-[0.95rem]',
            rightSlot && 'pr-12',
            error && 'border-rose-400/55',
          )}
        />
        {rightSlot && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
      {error && (
        <span id={errorId} className={cn(MICRO, 'text-rose-300/85')}>
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * One tick. Extracted because the form now has three of them and a copy of
 * this markup per checkbox is how two of them end up looking different.
 */
export function Check({
  id,
  checked,
  onChange,
  label,
}: {
  /**
   * Needed so the form can ADOPT a tick that landed before React attached —
   * see the adoption effect in `register-page.client.tsx`. A controlled
   * checkbox has no memory of a click it did not hear.
   */
  id: string
  checked: boolean
  onChange: (next: boolean) => void
  label: React.ReactNode
}) {
  return (
    <label
      className={cn(MICRO, 'flex cursor-pointer items-start gap-2.5 leading-relaxed text-white/65')}
    >
      {/*
        UNCONTROLLED, exactly like the text fields beside it and for the same
        reason. A controlled checkbox is RESTORED by React on hydration: a
        consent ticked in the first second is set in the DOM, wiped when React
        attaches, and never seen by anyone — so the adoption effect that
        rescues the typed fields had nothing left to read. `defaultChecked`
        lets the tick survive long enough to be adopted.

        Nothing sets these programmatically, so React never needs to push a
        value back down; the tick you see is drawn from `checked` below and
        stays in step through `onChange`.
      */}
      <input
        id={id}
        type="checkbox"
        defaultChecked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-all',
          checked ? 'border-transparent' : 'border-white/20 bg-white/[0.04]',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-white/70',
        )}
        style={checked ? { background: AUTH_ACCENT_GRADIENT } : undefined}
      >
        {checked && (
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none">
            <title>Zaznaczone</title>
            <path
              d="M5 12.5l4.5 4.5L19 7.5"
              stroke="white"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span>{label}</span>
    </label>
  )
}

/**
 * A document link inside a consent line. New tab: reading the terms must not
 * throw away a half-filled registration form.
 *
 * The colour is fixed here rather than passed in. It was `text-pink-300` handed
 * down from the page — Tailwind's pink, not the brand's, and the last of the
 * four private spellings `/pl/register` was holding.
 */
export function DocLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener"
      onClick={(e) => e.stopPropagation()}
      className="font-semibold text-white/72 underline-offset-4 transition-colors hover:text-white hover:underline"
    >
      {children}
    </Link>
  )
}
